import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { POSTS, getPostBySlug } from "../data/blog";
import useSeo from "../hooks/useSeo";
import { SITE_URL } from "../lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  // Hooks must run unconditionally — compute SEO args, then short-circuit render
  const canonical = `${SITE_URL}/blog/${slug}`;
  const wordCount = post
    ? [
        post.intro || "",
        ...(post.sections || []).map((s) => `${s.heading} ${s.body || ""}`),
        post.conclusion || "",
      ]
        .join(" ")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length
    : 0;
  useSeo({
    title: post ? post.title : "Post not found | ZOZO Venture",
    description: post?.description || "Startup guides for Indian founders by ZOZO Venture.",
    canonical,
    image: post?.cover,
    type: "article",
    jsonLd: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            image: post.cover,
            datePublished: post.date,
            dateModified: post.date,
            wordCount,
            timeRequired: `PT${post.readMinutes}M`,
            articleSection: post.tag,
            keywords: post.tag,
            inLanguage: "en-IN",
            author: { "@type": "Organization", name: post.author, url: `${SITE_URL}/` },
            publisher: {
              "@type": "Organization",
              name: "ZOZO Venture",
              url: `${SITE_URL}/`,
              logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/logo.png`,
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: canonical },
            ],
          },
        ]
      : null,
  });

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = (() => {
    const others = POSTS.filter((p) => p.slug !== slug);
    const sameTag = others.filter((p) => p.tag === post.tag);
    const rest = others.filter((p) => p.tag !== post.tag);
    return [...sameTag, ...rest].slice(0, 2);
  })();

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] font-body" data-testid="blog-post-page">
      <Header />
      <main>
        <article>
          <header className="border-b border-black/10 bg-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-10">
              <Link
                to="/blog"
                data-testid="back-to-blog"
                className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-black/60 hover:text-[#EAB308] transition-colors"
              >
                <ArrowLeft size={14} /> Back to Blog
              </Link>
              <div className="mt-6 flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-black/60">
                <span className="bg-[#EAB308] text-white px-2 py-0.5">{post.tag}</span>
                <span className="inline-flex items-center gap-1"><CalendarDays size={12} />{formatDate(post.date)}</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} />{post.readMinutes} min</span>
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                {post.title}
              </h1>
              <p className="mt-6 text-lg text-black/70 leading-relaxed">
                {post.description}
              </p>
            </div>
          </header>

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="relative aspect-[16/8] border border-black/80 overflow-hidden" data-testid="post-cover">
              <img src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <p className="text-lg sm:text-xl leading-relaxed text-black/85" data-testid="post-intro">
              {post.intro}
            </p>

            {post.sections.map((s, i) => (
              <section key={s.heading} className="mt-10" data-testid={`post-section-${i}`}>
                <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
                  {s.heading}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-black/80">
                  {s.body}
                </p>
                {s.table && (
                  <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto" data-testid={`post-table-${i}`}>
                    <table className="min-w-full border border-black/15 text-sm">
                      <thead className="bg-[#F4F4F5]">
                        <tr>
                          {s.table.headers.map((h) => (
                            <th
                              key={h}
                              scope="col"
                              className="border border-black/15 px-3 py-2 text-left font-heading font-bold text-black"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, ri) => (
                          <tr key={row[0] || ri} className={ri % 2 === 1 ? "bg-white" : "bg-[#FEFCF3]"}>
                            {row.map((cell, ci) => (
                              <td
                                key={ci}
                                className={`border border-black/15 px-3 py-2 align-top text-black/80 ${ci === 0 ? "font-semibold text-black" : ""}`}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            <section className="mt-12 border-t border-black/15 pt-10" data-testid="post-conclusion">
              <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
                The bottom line
              </h2>
              <p className="mt-4 text-base leading-relaxed text-black/80">
                {post.conclusion}
              </p>
            </section>

            <div className="mt-12 border-2 border-black p-8 bg-[#F4F4F5]">
              <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#EAB308]">
                Next step
              </p>
              <h3 className="font-heading font-black text-2xl sm:text-3xl mt-2 leading-tight">
                Ready to move from reading to doing?
              </h3>
              <p className="mt-3 text-black/70">
                Book a free 20-minute consultation — a ZOZO advisor will map the
                exact next steps for your specific situation.
              </p>
              <Link
                to="/#lead-form"
                data-testid="post-cta"
                className="mt-5 inline-flex items-center gap-2 bg-[#EAB308] text-white px-5 py-3 font-semibold hover:bg-black transition-colors"
              >
                Request free consultation →
              </Link>
            </div>
          </div>

          {related.length > 0 && (
            <section className="border-t border-black/10 bg-[#F4F4F5]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#EAB308]">
                  Keep reading
                </p>
                <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-3">
                  Related guides
                </h2>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                  {related.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/blog/${p.slug}`}
                      data-testid={`related-${p.slug}`}
                      className="group border border-black/80 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:neo-shadow-sm"
                    >
                      <span className="inline-block bg-[#EAB308] text-white text-[10px] tracking-[0.18em] uppercase px-2 py-0.5">
                        {p.tag}
                      </span>
                      <h3 className="font-heading font-bold text-xl mt-3 leading-tight">{p.title}</h3>
                      <p className="mt-2 text-sm text-black/70 line-clamp-2">{p.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}

function formatDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
